/**
 * Runtime configuration loader.
 *
 * Goal: build ONE image and deploy it to ANY environment. Vite normally inlines
 * `VITE_*` variables into the bundle at build time, which would lock an image to
 * a single environment. To avoid that, deployed environments inject their values
 * at container start via `window.__TOWER_CONFIG__` (served from `/config.js`,
 * rendered from a Kubernetes ConfigMap — see infra/helm/tower-web).
 *
 * Resolution order:
 *   1. `window.__TOWER_CONFIG__` (present only in deployed containers) — authoritative.
 *   2. `import.meta.env.VITE_*` (local `pnpm run dev` / `.env` files) — fallback.
 */

export interface TowerRuntimeConfig {
  TOWER_API_BASE_URL: string;
  TOWER_API_TIMEOUT_MS: string;
  /** OIDC issuer / authority base URL (provider-agnostic). */
  OIDC_AUTHORITY: string;
  /**
   * Explicit OIDC discovery document URL. Optional: when empty the client
   * derives `{authority}/.well-known/openid-configuration`. Set it when the
   * provider serves discovery off a non-default path (e.g. Entra External ID
   * exposes it under `/v2.0/`).
   */
  OIDC_METADATA_URL: string;
  /** Public OIDC client (application) id. */
  OIDC_CLIENT_ID: string;
  /**
   * Space-delimited scope string requested at login, e.g.
   * `openid profile offline_access api://tower-api/Tower.ApiAccess`.
   * `offline_access` is required so renewal uses the refresh-token grant.
   */
  OIDC_SCOPE: string;
  /** Base URL of the Tower.Agent service (epic #481). Empty disables the agent UI. */
  TOWER_AGENT_BASE_URL: string;
  /** Feature flag for the agent drawer ("true" to show it). */
  TOWER_AGENT_ENABLED: string;
  /** Public PostHog project token. */
  POSTHOG_PROJECT_TOKEN: string;
  /** PostHog event ingestion host. */
  POSTHOG_HOST: string;
  /**
   * Deployment environment name (e.g. "dev", "production"). The Docker image is
   * built once with `vite build` (always mode "production") and deployed
   * unchanged to every cluster, so `import.meta.env.MODE` can't tell them apart —
   * this must come from the injected runtime config instead.
   */
  TOWER_ENVIRONMENT: string;
}

declare global {
  interface Window {
    __TOWER_CONFIG__?: Partial<TowerRuntimeConfig>;
  }
}

const injectedConfig = (): Partial<TowerRuntimeConfig> | undefined =>
  globalThis.window?.__TOWER_CONFIG__;

// `import.meta.env` is only defined when bundled by Vite. Guard the access so
// this module can also be imported in a plain Node runtime (e.g. unit tests)
// without throwing. Under Vite this is statically replaced with the env object.
const viteEnv: Record<string, string | undefined> = import.meta.env ?? {};

/**
 * Reads a single config value. When running in a deployed container, the
 * injected config is authoritative (even empty strings are honored). In local
 * development it falls back to the build-time `VITE_*` value.
 *
 * The injected config is read on each access rather than captured once at
 * module load, so resolution never depends on whether `/config.js` ran before
 * this module was first imported.
 */
function read(key: keyof TowerRuntimeConfig, viteValue: string | undefined): string {
  const injected = injectedConfig();
  if (injected) {
    const value = injected[key];
    // Injected values originate from Helm values → JSON, where an unquoted YAML
    // scalar (e.g. TOWER_API_TIMEOUT_MS: 15000) would arrive as a number/boolean.
    // Coerce defensively so downstream string operations (.trim(), etc.) are safe.
    return value === undefined || value === null ? "" : String(value);
  }

  return viteValue ?? "";
}

export const runtimeConfig: TowerRuntimeConfig = {
  get TOWER_API_BASE_URL() {
    return read("TOWER_API_BASE_URL", viteEnv.VITE_TOWER_API_BASE_URL);
  },
  get TOWER_API_TIMEOUT_MS() {
    return read("TOWER_API_TIMEOUT_MS", viteEnv.VITE_TOWER_API_TIMEOUT_MS);
  },
  get OIDC_AUTHORITY() {
    return read("OIDC_AUTHORITY", viteEnv.VITE_OIDC_AUTHORITY);
  },
  get OIDC_METADATA_URL() {
    return read("OIDC_METADATA_URL", viteEnv.VITE_OIDC_METADATA_URL);
  },
  get OIDC_CLIENT_ID() {
    return read("OIDC_CLIENT_ID", viteEnv.VITE_OIDC_CLIENT_ID);
  },
  get OIDC_SCOPE() {
    return read("OIDC_SCOPE", viteEnv.VITE_OIDC_SCOPE);
  },
  get TOWER_AGENT_BASE_URL() {
    return read("TOWER_AGENT_BASE_URL", viteEnv.VITE_TOWER_AGENT_BASE_URL);
  },
  get TOWER_AGENT_ENABLED() {
    return read("TOWER_AGENT_ENABLED", viteEnv.VITE_TOWER_AGENT_ENABLED);
  },
  get POSTHOG_PROJECT_TOKEN() {
    return read("POSTHOG_PROJECT_TOKEN", viteEnv.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN);
  },
  get POSTHOG_HOST() {
    return read("POSTHOG_HOST", viteEnv.VITE_PUBLIC_POSTHOG_HOST);
  },
  get TOWER_ENVIRONMENT() {
    return read("TOWER_ENVIRONMENT", viteEnv.MODE);
  },
};
